import { Eraser, Squircle } from "lucide-react";
import { useT } from "@/src/i18n";
import {
  PATCH_SHAPES,
  TERRAIN_PATCH_CATALOG,
  type PatchShape,
} from "@/src/core/engine/terrainPatches";
import { useMapStore } from "@/src/store/useMapStore";

const SHAPE_LABEL: Record<PatchShape, "patch.shapeRect" | "patch.shapeEllipse" | "patch.shapePolygon"> = {
  rect: "patch.shapeRect",
  ellipse: "patch.shapeEllipse",
  polygon: "patch.shapePolygon",
};

export function PatchDrawer() {
  const selected = useMapStore((state) => state.selectedPatchType);
  const setSelected = useMapStore((state) => state.setSelectedPatchType);
  const shape = useMapStore((state) => state.patchShape);
  const setShape = useMapStore((state) => state.setPatchShape);
  const eraseMode = useMapStore((state) => state.patchEraseMode);
  const setEraseMode = useMapStore((state) => state.setPatchEraseMode);
  const patchCount = useMapStore((state) => state.terrainPatches.length);
  const t = useT();

  return (
    <aside className="flex max-h-full w-64 flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#1c1b18]/97 shadow-2xl shadow-black/30 backdrop-blur">
      <div className="shrink-0 flex items-start gap-2 border-b border-white/8 px-4 py-3">
        <Squircle size={15} className="mt-0.5 text-[#d75c51]" />
        <div>
          <p className="text-xs font-semibold text-stone-100">{t("patch.title")}</p>
          <p className="mt-1 text-[10px] leading-4 text-stone-500">{t("patch.subtitle")}</p>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="p-3 pb-0">
        <p className="mb-1.5 px-1 text-[9px] font-bold uppercase tracking-[0.16em] text-stone-600">
          {t("patch.shape")}
        </p>
        <div className="flex gap-1 rounded-xl bg-white/[0.035] p-1">
          {PATCH_SHAPES.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setShape(option)}
              data-patchshape={option}
              aria-pressed={shape === option}
              className={`h-8 flex-1 rounded-lg text-[9px] font-semibold transition ${shape === option ? "bg-[#a8322c] text-white" : "text-stone-400 hover:text-white"}`}
            >
              {t(SHAPE_LABEL[option])}
            </button>
          ))}
        </div>
      </div>

      <div className="p-3 pb-0">
        <button
          type="button"
          onClick={() => setEraseMode(!eraseMode)}
          data-testid="patch-erase"
          aria-pressed={eraseMode}
          className={`flex h-10 w-full items-center justify-center gap-2 rounded-xl border text-[10px] font-semibold transition ${eraseMode ? "border-[#d45a50] bg-[#a8322c] text-white" : "border-red-400/20 bg-red-500/[0.05] text-red-300/80 hover:border-red-400/35 hover:bg-red-500/10 hover:text-red-200"}`}
        >
          <Eraser size={14} /> {eraseMode ? t("patch.eraseActive") : t("patch.erase")}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-1.5 p-3">
        {TERRAIN_PATCH_CATALOG.map((patch) => {
          const active = !eraseMode && selected === patch.id;
          return (
            <button
              key={patch.id}
              type="button"
              onClick={() => setSelected(patch.id)}
              aria-pressed={active}
              data-patchtype={patch.id}
              className={`flex min-h-16 flex-col items-center justify-center gap-1 rounded-xl border p-1.5 text-center transition ${active ? "border-[#c3463d] bg-[#a8322c]/25 text-white" : "border-white/6 bg-white/[0.025] text-stone-400 hover:border-white/15 hover:bg-white/[0.06] hover:text-stone-100"}`}
            >
              <span
                className="grid h-7 w-12 place-items-center rounded-md border border-black/20 text-[8px] font-bold text-[#2b241d]"
                style={{ backgroundColor: patch.fill }}
              >
                {patch.pattern === "wave" ? "≈≈" : patch.pattern === "hatch" ? "///" : "∴∴"}
              </span>
              <span className="text-[9px] font-medium leading-none">{t(patch.labelKey)}</span>
            </button>
          );
        })}
      </div>

      </div>
      <div className="shrink-0 border-t border-white/8 px-4 py-2.5 text-[9px] text-stone-500">
        {t("patch.count", { count: patchCount })}
      </div>
    </aside>
  );
}
